import React, { useMemo, useState } from "react";
import { CaretUp, CaretDown, DownloadSimple, FilePdf } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export function ReportToolbar({ title, subtitle, onCsv, onPdf, children }) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {children}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCsv} data-testid="report-export-csv"><DownloadSimple className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button variant="outline" size="sm" onClick={onPdf} data-testid="report-export-pdf"><FilePdf className="h-4 w-4 mr-1.5" /> PDF</Button>
        </div>
      </div>
    </div>
  );
}

export default function SortableTable({ columns, rows, initialSort, testId }) {
  const [sort, setSort] = useState(initialSort || { key: null, dir: "asc" });

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const val = (r) => (col.sortValue ? col.sortValue(r) : col.value ? col.value(r) : r[sort.key]);
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (typeof av === "number" && typeof bv === "number") return sort.dir === "asc" ? av - bv : bv - av;
      return sort.dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [rows, sort, columns]);

  const toggle = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));
  const align = (a) => (a === "right" ? "text-right" : a === "center" ? "text-center" : "");

  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            {columns.map((c) => (
              <TableHead
                key={c.key}
                onClick={() => c.sortable !== false && toggle(c.key)}
                className={`whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500 ${c.sortable !== false ? "cursor-pointer select-none hover:text-slate-800" : ""} ${align(c.align)}`}
                data-testid={testId ? `${testId}-head-${c.key}` : undefined}
              >
                <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "justify-end w-full" : ""}`}>
                  {c.label}
                  {sort.key === c.key && (sort.dir === "asc" ? <CaretUp className="h-3 w-3" weight="bold" /> : <CaretDown className="h-3 w-3" weight="bold" />)}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow><TableCell colSpan={columns.length} className="text-center py-10 text-slate-400">No data for the selected filters.</TableCell></TableRow>
          ) : sorted.map((r, i) => (
            <TableRow key={r.id || i} className={r.__rowClass || ""} data-testid={testId ? `${testId}-row-${i}` : undefined}>
              {columns.map((c) => (
                <TableCell key={c.key} className={`py-2.5 text-sm whitespace-nowrap ${align(c.align)}`}>
                  {c.render ? c.render(r) : c.value ? c.value(r) : r[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
